import { useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { staffGroupsActions } from '@/store';
import { staffGroups as staffGroupsApi } from '@/lib/trackizy';
import useFeatures from '@/hooks/useFeatures';

/**
 * Staff groups for the current org, loaded once per org into the shared
 * Redux slice. The slice is cleared by the ROOT reducer on any org change and
 * on logout (plus socket.js's org-switch reset). A writer that makes member
 * counts stale (StaffDrawer save, StaffGroupSelect create/delete, the People
 * bulk bar) dispatches `invalidate` instead — items stay, `loaded` flips false
 * and every mounted instance refetches; a failed refetch keeps the last-known
 * list and retries on the next `enabled`/org change or remount. `enabled` is the
 * caller's own gate (admin/manager surfaces only): the route is
 * Workforce-gated and admin/manager-only (403 for anyone else), so a viewer
 * or a non-Workforce org must never fetch.
 *
 * A response is dispatched only if the org it was requested for is still the
 * active one — read from the store at resolve time, because a GET in flight
 * across an org switch (or logout) must not populate the new org's slice.
 */
export default function useStaffGroups({ enabled = true } = {}) {
  const dispatch = useDispatch();
  const store = useStore();
  const { workforce } = useFeatures();
  const orgId = useSelector((s) => s.org?.id ?? null);
  const items = useSelector((s) => s.staffGroups.items);
  const loaded = useSelector((s) => s.staffGroups.loaded);

  useEffect(() => {
    if (!enabled || !workforce || loaded) return undefined;
    let cancelled = false;
    const requestedFor = orgId;
    staffGroupsApi.list()
      .then((res) => {
        if (cancelled || (store.getState().org?.id ?? null) !== requestedFor) return;
        dispatch(staffGroupsActions.refresh(Array.isArray(res?.groups) ? res.groups : []));
      })
      .catch(() => { /* leave unloaded; the next mount retries */ });
    return () => { cancelled = true; };
  }, [enabled, workforce, loaded, orgId, dispatch, store]);

  const groups = Object.values(items).sort((a, b) => a.name.localeCompare(b.name));
  return { groups, loaded };
}
